import { useLanguage } from "../contexts/LanguageContext";
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "../Components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../Components/ui/card";
import { Input } from "../Components/ui/input";
import { useAuth } from "../contexts/AuthContext";
import { 
  Mail, 
  Lock, 
  User, 
  Eye, 
  EyeOff, 
  ArrowRight,
  Calendar,
  CheckCircle,
  Shield,
  RefreshCw
} from "lucide-react";

export default function Auth() {
  const { tx } = useLanguage();
  const { login } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [registerStep, setRegisterStep] = useState(1); // 1: Enter email, 2: Enter verification code, 3: Set password
  const [verificationCode, setVerificationCode] = useState("");

  // Add noindex meta tag for SEO
  useEffect(() => {
    const metaRobots = document.createElement('meta');
    metaRobots.name = 'robots';
    metaRobots.content = 'noindex, nofollow';
    document.head.appendChild(metaRobots);
    
    return () => {
      document.head.removeChild(metaRobots);
    };
  }, []);
  const [countdown, setCountdown] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState(""); // "success" or "error"
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: ""
  });

  // Simulate user data storage (should use backend API in actual project)
  const [users, setUsers] = useState(() => {
    const saved = localStorage.getItem('intelevia_users');
    return saved ? JSON.parse(saved) : [];
  });

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // Generate 6-digit verification code
  const generateVerificationCode = () => {
    return Math.floor(100000 + Math.random() * 900000).toString();
  };

  // Send verification code
  const sendVerificationCode = async () => {
    if (!formData.email) {
      setMessage("Please enter your email address");
      setMessageType("error");
      return;
    }

    setIsLoading(true);
    setMessage("");

    // Simulate sending verification code (should call backend API in actual project)
    setTimeout(() => {
      const code = generateVerificationCode();
      // In actual project, this should send verification code via email service
      console.log("Verification code sent to", formData.email, "Code:", code);
      
      // Store verification code in sessionStorage (for demo only)
      sessionStorage.setItem('verification_code', code);
      sessionStorage.setItem('verification_email', formData.email);
      
      setMessage("Verification code has been sent to your email, please check");
      setMessageType("success");
      setRegisterStep(2);
      setCountdown(60);
      
      // Countdown timer
      const timer = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      setIsLoading(false);
    }, 1000);
  };

  // Verify verification code
  const verifyCode = () => {
    const storedCode = sessionStorage.getItem('verification_code');
    const storedEmail = sessionStorage.getItem('verification_email');
    
    if (storedEmail !== formData.email) {
      setMessage("Email address does not match");
      setMessageType("error");
      return;
    }
    
    if (verificationCode !== storedCode) {
      setMessage("Verification code is incorrect, please try again");
      setMessageType("error");
      return;
    }
    
    setMessage("Verification code verified successfully");
    setMessageType("success");
    setRegisterStep(3);
  };

  // Complete registration
  const completeRegistration = () => {
    if (formData.password !== formData.confirmPassword) {
      setMessage("Passwords do not match");
      setMessageType("error");
      return;
    }
    
    if (formData.password.length < 6) {
      setMessage("Password must be at least 6 characters long");
      setMessageType("error");
      return;
    }

    // Check if user already exists
    const existingUser = users.find(user => user.email === formData.email);
    if (existingUser) {
      setMessage("This email is already registered");
      setMessageType("error");
      return;
    }

    // Create new user
    const newUser = {
      id: Date.now(),
      name: formData.name,
      email: formData.email,
      password: formData.password, // Should be encrypted in actual project
      createdAt: new Date().toISOString()
    };

    const updatedUsers = [...users, newUser];
    setUsers(updatedUsers);
    localStorage.setItem('intelevia_users', JSON.stringify(updatedUsers));
    
    // Clear verification code
    sessionStorage.removeItem('verification_code');
    sessionStorage.removeItem('verification_email');
    
    setMessage("Registration successful! Please login");
    setMessageType("success");
    
    // Reset form and switch to login
    setTimeout(() => {
      setIsLogin(true);
      setRegisterStep(1);
      setFormData({ name: "", email: "", password: "", confirmPassword: "" });
      setVerificationCode("");
      setMessage("");
    }, 2000);
  };

  // Login
  const handleLogin = () => {
    const user = users.find(u => u.email === formData.email && u.password === formData.password);
    
    if (!user) {
      setMessage("Email or password is incorrect");
      setMessageType("error");
      return;
    }
    
    setMessage("Login successful!");
    setMessageType("success");
    
    // Use AuthContext to store login status
    login(user);
    
    // In actual project, should redirect to home page or dashboard
    setTimeout(() => {
      window.location.href = '/';
    }, 1000);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setMessage("");
    
    if (isLogin) {
      handleLogin();
    } else {
      if (registerStep === 1) {
        sendVerificationCode();
      } else if (registerStep === 2) {
        verifyCode();
      } else if (registerStep === 3) {
        completeRegistration();
      }
    }
  };

  // Resend verification code
  const resendCode = () => {
    if (countdown > 0) return;
    sendVerificationCode();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-green-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-1/4 left-1/4 w-72 h-72 bg-emerald-500 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-green-500 rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center animate-slide-in-up">
          <div className="inline-flex items-center px-4 py-2 bg-emerald-100 text-emerald-800 rounded-full text-sm font-medium mb-6">
            <span className="w-2 h-2 bg-emerald-500 rounded-full mr-2 animate-pulse"></span>{tx("Welcome to Intelevia")}{" "}</div>
          
          <div className="flex items-center justify-center mb-6">
            <div className="w-16 h-16 bg-white rounded-2xl shadow-lg flex items-center justify-center animate-float">
              <Calendar className="w-8 h-8 text-emerald-600" />
            </div>
          </div>
          
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            {tx(isLogin ? "Welcome Back" :
             registerStep === 1 ? "Create Account" :
             registerStep === 2 ? "Verify Email" : "Set Password")}
          </h2>
          <p className="text-gray-600">
            {tx(isLogin
              ? "Sign in to your account to continue" 
              : registerStep === 1 ? "Join thousands of users who trust Kylendar" :
                registerStep === 2 ? "Enter the verification code sent to your email" :
                "Create a secure password for your account")
            }
          </p>
        </div>

        {/* Auth Form */}
        <Card className="border-0 shadow-2xl animate-scale-in animate-delay-200">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl text-center text-gray-900">
              {tx(isLogin ? "Sign In" :
               registerStep === 1 ? "Sign Up" :
               registerStep === 2 ? "Email Verification" : "Set Password")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Message notification */}
            {message && (
              <div className={`p-3 rounded-lg text-sm ${
                messageType === "success" 
                  ? "bg-green-50 text-green-700 border border-green-200" 
                  : "bg-red-50 text-red-700 border border-red-200"
              }`}>
                {tx(message)}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Registration Step 1: Enter name and email */}
              {!isLogin && registerStep === 1 && (
                <>
                  <div className="space-y-2 animate-fade-in animate-delay-300">
                    <label htmlFor="name" className="text-sm font-medium text-gray-700">{tx("Full Name")}</label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={handleInputChange}
                        className="pl-10"
                        placeholder={tx("Enter your full name")}
                        onInvalid={(e) => e.target.setCustomValidity(tx('Please enter your full name'))}
                        onInput={(e) => e.target.setCustomValidity('')}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="email" className="text-sm font-medium text-gray-700">{tx("Email Address")}</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleInputChange}
                        className="pl-10"
                        placeholder={tx("Enter your email")}
                        onInvalid={(e) => e.target.setCustomValidity(tx('Please enter a valid email address'))}
                        onInput={(e) => e.target.setCustomValidity('')}
                      />
                    </div>
                  </div>
                </>
              )}

              {/* Registration Step 2: Enter verification code */}
              {!isLogin && registerStep === 2 && (
                <>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">{tx("Verification Code")}</label>
                    <div className="relative">
                      <Shield className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <Input
                        type="text"
                        value={verificationCode}
                        onChange={(e) => setVerificationCode(e.target.value)}
                        className="pl-10"
                        placeholder={tx("Enter 6-digit code")}
                        maxLength="6"
                      />
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">{tx("Code sent to:")}{" "}{formData.email}
                      </span>
                      <button
                        type="button"
                        onClick={resendCode}
                        disabled={countdown > 0}
                        className={`text-emerald-600 hover:text-emerald-500 ${
                          countdown > 0 ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                      >
                        {countdown > 0 ? tx('Resend in {seconds}s', { seconds: countdown }) : tx('Resend Code')}
                      </button>
                    </div>
                  </div>
                </>
              )}

              {/* Registration Step 3: Set password */}
              {!isLogin && registerStep === 3 && (
                <>
                  <div className="space-y-2">
                    <label htmlFor="password" className="text-sm font-medium text-gray-700">{tx("Password")}</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <Input
                        id="password"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        required
                        value={formData.password}
                        onChange={handleInputChange}
                        className="pl-10 pr-10"
                        placeholder={tx("Enter your password (min 6 characters)")}
                        onInvalid={(e) => e.target.setCustomValidity(tx('Please enter your password'))}
                        onInput={(e) => e.target.setCustomValidity('')}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="confirmPassword" className="text-sm font-medium text-gray-700">{tx("Confirm Password")}</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <Input
                        id="confirmPassword"
                        name="confirmPassword"
                        type={showPassword ? "text" : "password"}
                        required
                        value={formData.confirmPassword}
                        onChange={handleInputChange}
                        className="pl-10"
                        placeholder={tx("Confirm your password")}
                        onInvalid={(e) => e.target.setCustomValidity(tx('Please confirm your password'))}
                        onInput={(e) => e.target.setCustomValidity('')}
                      />
                    </div>
                  </div>
                </>
              )}

              {/* Login form */}
              {isLogin && (
                <>
                  <div className="space-y-2">
                    <label htmlFor="email" className="text-sm font-medium text-gray-700">{tx("Email Address")}</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleInputChange}
                        className="pl-10"
                        placeholder={tx("Enter your email")}
                        onInvalid={(e) => e.target.setCustomValidity(tx('Please enter a valid email address'))}
                        onInput={(e) => e.target.setCustomValidity('')}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="password" className="text-sm font-medium text-gray-700">{tx("Password")}</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <Input
                        id="password"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        required
                        value={formData.password}
                        onChange={handleInputChange}
                        className="pl-10 pr-10"
                        placeholder={tx("Enter your password")}
                        onInvalid={(e) => e.target.setCustomValidity(tx('Please enter your password'))}
                        onInput={(e) => e.target.setCustomValidity('')}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                </>
              )}

              {isLogin && (
                <div className="flex items-center justify-between">
                  <label className="flex items-center">
                    <input type="checkbox" className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500" />
                    <span className="ml-2 text-sm text-gray-600">{tx("Remember me")}</span>
                  </label>
                  <Link to="/forgot-password" className="text-sm text-emerald-600 hover:text-emerald-500">{tx("Forgot password?")}</Link>
                </div>
              )}

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full gradient-green text-white hover:opacity-90 group animate-pulse-glow disabled:opacity-50"
              >
                {isLoading ? (
                  <>
                    <RefreshCw className="w-5 h-5 mr-2 animate-spin" />{tx("Processing...")}{" "}</>
                ) : (
                  <>
                    {tx(isLogin ? "Sign In" :
                     registerStep === 1 ? "Send Verification Code" :
                     registerStep === 2 ? "Verify Code" : "Create Account")}
                    <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </Button>
            </form>



            {/* Toggle Auth Mode */}
            <div className="text-center">
              <p className="text-sm text-gray-600">
                {tx(isLogin ? "Don't have an account?" : "Already have an account?")}
                <button
                  onClick={() => {
                    setIsLogin(!isLogin);
                    setRegisterStep(1);
                    setFormData({ name: "", email: "", password: "", confirmPassword: "" });
                    setVerificationCode("");
                    setMessage("");
                    setCountdown(0);
                  }}
                  className="ml-1 text-emerald-600 hover:text-emerald-500 font-medium"
                >
                  {tx(isLogin ? "Sign up" : "Sign in")}
                </button>
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Features */}
        <div className="grid grid-cols-3 gap-4 text-center animate-fade-in animate-delay-500">
          <div className="flex flex-col items-center space-y-2">
            <CheckCircle className="w-6 h-6 text-emerald-600 animate-float" />
            <span className="text-sm text-gray-600">{tx("Secure")}</span>
          </div>
          <div className="flex flex-col items-center space-y-2">
            <CheckCircle className="w-6 h-6 text-emerald-600 animate-float animate-delay-200" />
            <span className="text-sm text-gray-600">{tx("Fast")}</span>
          </div>
          <div className="flex flex-col items-center space-y-2">
            <CheckCircle className="w-6 h-6 text-emerald-600 animate-float animate-delay-400" />
            <span className="text-sm text-gray-600">{tx("Reliable")}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
